import { config } from '../../../game/defs/config';
import { GoalId } from '../../../game/defs/goal';
import { GoalsDef, RewardsDef } from '../../../game/defs/map';
import { mysteryItemRewardPropsMap } from '../../../game/defs/reward';
import { mapGetStats } from '../../../game/util/mapTools';
import { TabScreenOptions } from '../../../odie/plugins/nav/TabNav';
import { BasicText } from '../../../lib/ui/BasicText';
import { EditorScreen } from '../../EditorScreen';
import { EditorGoalPicker } from './EditorGoalPicker';
import { BasicTabScreen } from '../../../lib/ui/BasicTabScreen';
import { NumberPicker } from '../../../lib/ui/NumberPicker';
import { BlockId, blockPropsMap } from '../../../game/defs/block';
import { ScrollView } from '@play-co/timestep-core/ui';

// constants
//-----------------------------------------------------------------------------
// public
export type EditorGoalMenuOptions = {
  screen: EditorScreen;
};

// private
const styles = {
  contentX: 150,
  contentMarginY: 10,
};

/*
    puzzle editor goal menu
*/
export class EditorGoalMenu extends BasicTabScreen {
  // fields
  //-------------------------------------------------------------------------
  // input
  private _screen!: EditorScreen;
  // scene
  private readonly _goalPickers: EditorGoalPicker[] = [];
  private _movesPicker!: NumberPicker;
  // state
  private _y = 16;

  private _autoUpdate = false;
  private _scroller!: ScrollView;

  // properties
  //-------------------------------------------------------------------------
  public static assets(): string[] {
    return [
      ...NumberPicker.assets(),
      ...EditorGoalPicker.assets(),
      ...Object.values(mysteryItemRewardPropsMap).map((prop) => prop.icon),
    ];
  }

  public get goals(): GoalsDef {
    // return picker props as goal defs
    return this._goalPickers.reduce((goals, picker) => {
      if (picker.count > 0) {
        goals[picker.goalId] = picker.count;
      }

      return goals;
    }, {} as GoalsDef);
  }

  public set goals(goalDef: GoalsDef) {
    // reset existing pickers
    for (const picker of this._goalPickers) {
      picker.goalId = 'basicBlue';
      picker.count = 0;
    }

    // for each goal def, update the next picker
    let pickerIndex = 0;

    for (const [goalId, count] of Object.entries(goalDef)) {
      const picker = this._goalPickers[pickerIndex];

      if (!picker) {
        console.warn('Editor cannot display this many goals.');
        break;
      }

      picker.goalId = goalId as GoalId;
      picker.count = count;
      ++pickerIndex;
    }

    // update
    this._updateUsedCounts();
  }

  public clear() {
    this.goals = {};
    this.moves = 40;
  }

  public get moves(): number {
    return this._movesPicker.value;
  }

  public set moves(count: number) {
    this._movesPicker.value = count;
  }

  public set rewards(rewardsDef: RewardsDef) {
    // for each reward def, update picker
  }

  public get rewards(): RewardsDef {
    const rewardsDef: RewardsDef = {};
    return rewardsDef;
  }

  // init
  //-------------------------------------------------------------------------
  constructor(options: TabScreenOptions) {
    super(options);

    // spawn scrroller
    this._spawnScroller();

    // spawn moves picker
    this._spawnMovesPicker();

    // spawn goal pickers
    this._spawnGoalPickers();
  }

  public async prepare(options: EditorGoalMenuOptions) {
    // set fields
    this._screen = options.screen;

    // update
    this._updateUsedCounts();
    if (this._autoUpdate) this.autoUpdate();
  }

  private _getMapStats(screen?: EditorScreen) {
    const _screen = screen ?? this._screen;
    if (!_screen) return null;
    return mapGetStats(_screen.getMapDef(), true);
  }

  // private: spawn
  //-------------------------------------------------------------------------
  private _spawnScroller() {
    // spawn scroller
    this._scroller = new ScrollView({
      superview: this.view.view,
      width: this.width,
      height: this.height,
      backgroundColor: 'black',
      scrollX: false,
      scrollY: true,
    });

    this._scroller.setScrollBounds({
      minY: 0,
      maxY: 100 + config.goals.goalsLimit * 75,
    });
  }

  private _spawnMovesPicker() {
    // spawn title
    this._spawnTitle(this._y, 'MOVES');

    // create moves picker
    const moves = (this._movesPicker = new NumberPicker({
      value: 50,
      min: config.moves.min,
      max: config.moves.max,
      width: 230,
      height: 55,
    }));
    moves.x = styles.contentX;
    moves.y = this._y;

    // spawn
    this._scroller.addSubview(moves.view);
    this._y = moves.y + moves.height + 5;
  }

  private _spawnGoalPickers() {
    // spawn title
    this._spawnTitle(this._y, 'GOALS');

    // for each iteration up to goals limit
    for (let i = 0; i < config.goals.goalsLimit; ++i) {
      // spawn picker
      const picker = this._spawnGoalPicker();
      picker.y = this._y;
      this._y += picker.height + 5;

      // register events
      picker.onUpdated = () => {
        this._screen?.setAsUnsaved();
        this._updateUsedCounts();
      };

      // add to picker list
      this._goalPickers.push(picker);
    }
  }

  private _spawnGoalPicker(): EditorGoalPicker {
    // spawn goal picker
    const picker = new EditorGoalPicker({
      height: 55,
    });
    picker.x = styles.contentX;
    this._scroller.addSubview(picker.view);
    picker.onManuallyChanged = () => {
      this._screen?.setAsUnsaved();
    };

    return picker;
  }

  // private: factory
  //-------------------------------------------------------------------------
  private _spawnTitle(y: number, text: string) {
    // create text widget
    const widget = new BasicText({
      text,
      extraStyle: {
        fill: 'white',
        fontSize: 34,
        // lineJoin: 'round',
      },
    });

    widget.y = y;

    // spawn
    this._scroller.addSubview(widget.view);
  }

  // private: updaters
  //-------------------------------------------------------------------------
  private _updateUsedCounts(screen?: EditorScreen) {
    const mapStats = this._getMapStats(screen);
    if (!mapStats) return;

    // for each picker, update used count
    for (const picker of this._goalPickers) {
      // Skip empty pickers
      if (picker.isEmpty()) continue;

      const isAutoUpdate = picker.count === picker.usedCount;
      picker.usedCount = mapStats[picker.goalId]?.count || 0;
      picker.spawnable = this._isSpawnable(picker.goalId);

      // If goal is auto-updated, updates the count to reflect the new total used count
      // otherwise we can consider that it has been changed manually
      if (isAutoUpdate) {
        picker.count = picker.usedCount;
      }
      // If goal is not spawn-able, cap the count to the total used count to prevent impossible goals
      else if (!picker.spawnable) {
        picker.count = Math.min(picker.count, picker.usedCount);
      }
    }
  }

  private _isSpawnable(id: GoalId) {
    // For when goal is spawnable on the map by default
    const isSpawnable = !!this._screen?.spawnMenu.spawns.includes(
      id as BlockId,
    );

    const mapStats = this._screen
      ? mapGetStats(this._screen.getMapDef())
      : undefined;

    // For when goal itself is a generator
    const isGeneratorType = blockPropsMap[id as BlockId].generator;

    // For when goal is a custom goal of one of the spawnable blocks
    const isCustomGoal = this.isCustomGoalOfSpawnable(id);

    // For when goal are generated by one or more existing generator blocks defined on the map
    const isGeneratable =
      !isGeneratorType && mapStats
        ? !!Object.keys(mapStats).find((blockId) => {
            const props = blockPropsMap[blockId as BlockId];
            return props.generator && props.customGoal === id;
          })
        : false;

    // Spawnable blocks means that they should not have their goal pickers capped by the total
    // as they can be spawn or generated.
    return isSpawnable || isGeneratorType || isGeneratable || isCustomGoal;
  }

  /**
   * Find out if the goal is referenced by one of the spawnable blocks
   */
  private isCustomGoalOfSpawnable(goalId: GoalId) {
    const spawnables = this._screen?.spawnMenu.spawns ?? [];
    for (const spawnable of spawnables) {
      const props = blockPropsMap[spawnable];
      if (props.customGoal === goalId) return true;
    }
    return false;
  }

  private _isValid(id: GoalId) {
    const blockProps = blockPropsMap[id as BlockId];
    return !!blockProps && (!blockProps.immortal || blockProps.generator);
  }

  private _autoUpdateGoals(screen?: EditorScreen) {
    const mapStats = this._getMapStats(screen);
    if (!mapStats) return;

    // Loop through map stats and try to auto-update goal pickers
    for (const [id, value] of Object.entries(mapStats)) {
      // Some blocks should not count for auto-goal update
      if (id === 'bomb') continue;
      if (id.startsWith('basic')) continue;
      if (id.startsWith('rocket')) continue;
      if (id.startsWith('cube')) continue;

      const blockProps = blockPropsMap[id as BlockId];

      // Invalid blocks should not count as goals
      if (!this._isValid(id as GoalId)) continue;

      // Get the goald id for the block
      const goalId = (blockProps.sharedGoal ??
        blockProps.customGoal ??
        id) as GoalId;
      const multiplier = blockProps.goalCountMultiplier ?? 1;
      const count = value.count * multiplier;
      const isSpawnable = this._isSpawnable(goalId);

      // Find existing goal picker to auto-update
      const goalPicker = this._goalPickers.find((picker) => {
        return picker.goalId === goalId;
      });

      if (goalPicker) {
        goalPicker.spawnable = isSpawnable;
        continue;
      }

      // Find empty goal picker to auto-update
      const emptyGoalPicker = this._goalPickers.find((picker) => {
        return picker.isEmpty();
      });

      // Update the empty goal picker if with new id and count
      if (emptyGoalPicker) {
        emptyGoalPicker.goalId = goalId;
        emptyGoalPicker.spawnable = isSpawnable;
        emptyGoalPicker.usedCount = count;
        emptyGoalPicker.count = count;
      }
    }
  }

  private _cleanupPickers(screen?: EditorScreen) {
    const mapStats = this._getMapStats(screen);
    if (!mapStats) return;

    for (const picker of this._goalPickers) {
      const mapStatsItem = mapStats[picker.goalId];
      if (!mapStatsItem && !picker.usedCount && picker.count <= 1) {
        picker.goalId = 'basicBlue';
        picker.count = 0;
      }
    }
  }

  public autoUpdate(screen?: EditorScreen) {
    this._autoUpdate = true;
    this._autoUpdateGoals(screen);
    this._updateUsedCounts(screen);
    this._cleanupPickers(screen);
  }
}
